/**
 * Sound Fallback Service
 * Generates ethereal, celestial sounds for Iris goddess theme
 * Uses Web Audio API with harmonic overtones for divine quality
 */

import soundConfigService from '@/services/soundConfig';

export class SoundGenerator {
  private audioContext: AudioContext | null = null;

  getAudioContext(): AudioContext {
    if (!this.audioContext && typeof window !== 'undefined') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext!;
  }

  /**
   * Generate an ethereal beep with harmonics for celestial quality
   */
  playBeep(frequency: number = 800, duration: number = 0.15) {
    try {
      const audioContext = this.getAudioContext();
      const now = audioContext.currentTime;

      // Create master gain node
      const masterGain = audioContext.createGain();
      masterGain.connect(audioContext.destination);

      // Create fundamental frequency
      const fundamental = audioContext.createOscillator();
      const fundamentalGain = audioContext.createGain();
      fundamental.connect(fundamentalGain);
      fundamentalGain.connect(masterGain);
      fundamental.frequency.value = frequency;
      fundamental.type = 'sine';

      // Add harmonic overtones for divine, bell-like quality
      const harmonic2 = audioContext.createOscillator();
      const harmonic2Gain = audioContext.createGain();
      harmonic2.connect(harmonic2Gain);
      harmonic2Gain.connect(masterGain);
      harmonic2.frequency.value = frequency * 2; // Octave
      harmonic2.type = 'sine';

      const harmonic3 = audioContext.createOscillator();
      const harmonic3Gain = audioContext.createGain();
      harmonic3.connect(harmonic3Gain);
      harmonic3Gain.connect(masterGain);
      harmonic3.frequency.value = frequency * 3; // Perfect fifth above octave
      harmonic3.type = 'sine';

      // Ethereal envelope - gradual attack and smooth decay
      const attackTime = 0.02;
      const decayTime = duration * 0.3;
      const sustainLevel = 0.4;

      // Fundamental: strongest component
      fundamentalGain.gain.setValueAtTime(0, now);
      fundamentalGain.gain.linearRampToValueAtTime(0.35, now + attackTime);
      fundamentalGain.gain.exponentialRampToValueAtTime(
        sustainLevel * 0.35,
        now + attackTime + decayTime,
      );
      fundamentalGain.gain.exponentialRampToValueAtTime(0.01, now + duration);

      // Second harmonic: adds brightness
      harmonic2Gain.gain.setValueAtTime(0, now);
      harmonic2Gain.gain.linearRampToValueAtTime(0.15, now + attackTime);
      harmonic2Gain.gain.exponentialRampToValueAtTime(
        sustainLevel * 0.15,
        now + attackTime + decayTime,
      );
      harmonic2Gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

      // Third harmonic: adds shimmer
      harmonic3Gain.gain.setValueAtTime(0, now);
      harmonic3Gain.gain.linearRampToValueAtTime(0.08, now + attackTime);
      harmonic3Gain.gain.exponentialRampToValueAtTime(
        sustainLevel * 0.08,
        now + attackTime + decayTime,
      );
      harmonic3Gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

      // Start and stop all oscillators
      fundamental.start(now);
      fundamental.stop(now + duration);
      harmonic2.start(now);
      harmonic2.stop(now + duration);
      harmonic3.start(now);
      harmonic3.stop(now + duration);
    } catch (error) {
      console.warn('Could not play beep sound:', error);
    }
  }

  /**
   * Generate sound using server configuration
   */
  playSound(
    type: 'message' | 'mention' | 'join' | 'leave' | 'mute' | 'unmute' | 'notification' | 'ding',
  ) {
    try {
      const soundConfig = soundConfigService.getSound(type);

      if (soundConfig) {
        this.playBeep(soundConfig.frequency, soundConfig.duration);
      } else {
        console.warn(`Sound config not found for type: ${type}`);
      }
    } catch (error) {
      console.warn(`Error playing sound ${type}:`, error);
    }
  }
}

export default new SoundGenerator();
